import {Pagination} from '@store/common/pagination.model';
import {Currency} from '@store/common/organisation.enums';
import {OrganisationUnitLight} from '@store/common/organisation.model';
import {SelectItem} from '@components/inputs/select/Select.component';
import {ServiceKindLight} from '@store/settings/serviceKinds';
import {ManagePrice, Price} from '@store/common/price.model';
import {Waste} from '@store/settings/wasteFamilies';
import {Container} from '@store/settings/containers';
import {TableFilter, TableFilterType} from '@store/common/tableFilter.model';

export interface ManageSuppliersServiceAvailability {
  id?: number;
  availableZipCodes?: string[];
  availableFrom?: Date;
  availableTo?: Date;
}

export interface ManageSuppliersService {
  id?: number;
  supplier?: SelectItem;
  supplierName?: string;
  supplierUnit?: OrganisationUnitLight;
  serviceKind?: ServiceKindLight;
  waste?: SelectItem;
  container?: SelectItem;
  availabilities?: ManageSuppliersServiceAvailability[];
  prices?: ManagePrice[];
  wasUpdated?: boolean;
}

export interface SuppliersServiceAvailability {
  id: number;
  zipCodes: string[];
  fromDate: Date;
  toDate: Date;
}

export namespace SuppliersServiceAvailability {
  export const toManageSuppliersServiceAvailability = (availability: SuppliersServiceAvailability): ManageSuppliersServiceAvailability => ({
    id: availability.id,
    availableZipCodes: availability.zipCodes,
    availableFrom: availability.fromDate,
    availableTo: availability.toDate,
  });
}

export interface SuppliersServiceDetails {
  id: number;
  supplierName: string;
  supplierOrganisationUnit: OrganisationUnitLight;
  kind: ServiceKindLight;
  waste: Waste;
  container: Container;
  availabilities: SuppliersServiceAvailability[];
  buyPrices: Price[];
}

export namespace SuppliersServiceDetails {
  export const toManageSuppliersService = (service: SuppliersServiceDetails): ManageSuppliersService => ({
    id: service.id,
    supplier: service.supplierOrganisationUnit?.supplierOrganisation ? {
      label: service.supplierOrganisationUnit.supplierOrganisation.name,
      value: service.supplierOrganisationUnit.supplierOrganisation.id,
    } : undefined,
    supplierName: service.supplierName,
    supplierUnit: service.supplierOrganisationUnit,
    serviceKind: service.kind,
    waste: service.waste ? {
      label: service.waste.name,
      value: service.waste.id,
    } : undefined,
    container: service.container ? {
      label: service.container.name,
      value: service.container.id,
    } : undefined,
    availabilities: service.availabilities?.map(SuppliersServiceAvailability.toManageSuppliersServiceAvailability),
    prices: service.buyPrices?.map(Price.toManagePrice),
  });
}

export interface SuppliersServicesFilters {
  search: string;
  tableFilters: TableFilter[];
}

export namespace SuppliersServicesFilters {
  export const initTableFilters = (supplierId?: string) => TableFilterType.initTableFilters([
    TableFilterType.SUPPLIER_ORGANISATION,
    TableFilterType.SUPPLIER_ORGANISATION_UNIT,
    TableFilterType.WASTE_FAMILY,
    TableFilterType.WASTE,
    TableFilterType.CONTAINER,
    TableFilterType.SERVICE_KIND,
  ]).map((f) => ({
    ...f,
    hide: (!!supplierId && f.type === TableFilterType.SUPPLIER_ORGANISATION),
    values: (f.type === TableFilterType.SUPPLIER_ORGANISATION && !!supplierId) ? [{label: '', value: supplierId}] : [],
  }));
}

export interface SuppliersServicesPagination extends Pagination {
  data: SuppliersService[];
}

export interface SuppliersServiceUI {
  id: number;
  selected: boolean;
}

export interface SuppliersService {
  id: number;
  supplierName: string;
  supplierOrgName: string;
  supplierUnitName: string;
  kindName: string;
  kind: ServiceKindLight;
  availabilitiesCount: number;
  containerName: string;
  wasteName: string;
  buyPrices: Price[];
  buyPricesCount: number;
  buyPriceIfOnlyOne: number;
  buyPriceCurrency: Currency;
  waste: Waste;
  container: Container;
  supplierOrganisationUnit: OrganisationUnitLight;
}
